import React, {createContext, useContext, useEffect, useState} from "react";
import moment from "moment";
import storage from '../../../player/js/storage';
import {AVAILABLE_LOCALES, LANG_RU} from "../constants/common";
import { useWidgetContext } from "./WidgetContextProvider";
import "@platform/locale/default.ru.json";
import "@platform/locale/default.en.json";

export const LocaleContext = createContext(null);

export const useLocaleContext = () => useContext(LocaleContext);

const saveLanguageInStorage = (lang, eventKey) => {
    storage.set(`event/${eventKey}/lang`, lang, storage.MONTH);
};

const findLocaleInNavigator = () => {
    return window.navigator.languages
        .map((language) => language.split('-')[0].toLowerCase())
        .find((language) => -1 !== AVAILABLE_LOCALES.indexOf(language));
};

const findLocaleInRequest = (eventKey) => {
    const match = window.location.search.match(/(\?|&)lang=(\w{2})(&|$)/);
    let lang = match ? match[2] : null;

    if (eventKey) {
        saveLanguageInStorage(lang, eventKey);
    }

    return lang;
};

const findLocaleInStorage = (eventKey) => {
    return storage.get(`event/${eventKey}/lang`) || null;
};

const findSelectedLocale = (eventKey, eventLocale) =>
    findLocaleInRequest(eventKey) || eventLocale ||
    (eventKey ? findLocaleInStorage(eventKey) : null) ||
    findLocaleInNavigator();

export const LocaleContextProvider = ({defaultName = LANG_RU, children}) => {
    const { isWidget, widgetSettings } = useWidgetContext();

    const [name, setName] = useState(defaultName);
    const [locales, setLocales] = useState({});
    const [eventKey, setEventKey] = useState(null);
    const [eventLocale, setEventLocale] = useState(null);
    const [isLoadingLocale, setIsLoadingLocale] = useState(false);

    useEffect(() => {
        if (isWidget && !!widgetSettings) {
            setName(findLocaleInRequest(eventKey) || widgetSettings?.lang || findLocaleInNavigator());
        } else {
            setName(findSelectedLocale(eventKey, eventLocale) || defaultName);
        }
    }, [eventKey, eventLocale, isWidget, widgetSettings]);

    useEffect(() => {
        getLocale();
        moment.locale(name);
    }, [name]);

    async function getLocale(lang = name, namespace = 'default') {
        if (!locales[namespace] || !locales[namespace][lang]) {
            setIsLoadingLocale(true);
            try {
                const data = await import(`@platform/locale/${namespace}.${lang}.json`);
                setLocales({
                    ...locales,
                    [namespace]: {
                        ...locales[namespace],
                        [lang]: data.default,
                    }
                })
            } catch (error) {
                console.error(error);
            }

            setIsLoadingLocale(false);
        }

        return locales;
    }

    const context = {
        name,
        setLocaleName: setName,
        setEventLocale,
        get(key, params = {}, namespace = 'default') {
            const locale = locales[namespace][name] || {};

            let value = locale[key];
            if (typeof value === 'string') {
                value = value.replace(/{([-_\w]+)}/g, (interpolation, key) => params[key] ?? interpolation);
            }

            return locale.hasOwnProperty(key) ? value : key; // TODO: change to fallback
        },
        getProperty(object, key, defaultValue) {
            const camelCaseKey = key + name.substring(0, 1).toUpperCase() + name.substring(1).toLowerCase();
            if (!object) return defaultValue;
            if (object.hasOwnProperty(camelCaseKey)) {
                return object[camelCaseKey];
            }
            const snakeCaseKey = key + '_' + name.toLowerCase().substring(0, 2);
            if (object.hasOwnProperty(snakeCaseKey)) {
                return object[snakeCaseKey];
            }
            return defaultValue;
        },
        setEventKey,
        isLoadingLocale,
    };

    return (
        <LocaleContext.Provider value={context}>
            {children}
        </LocaleContext.Provider>
    );
}
